export type TrackingEventName =
  | "view_item"
  | "select_item"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase";

export type TrackingCheckpoint = string; // exemple: "select_date", "select_time", "enter_info", etc. 
/**
 * Structure d'un item GA4 Ecommerce (aligné Regiondo)
 */
export interface TrackingItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

/**
 * Payload GA4 Ecommerce standard
 */
export interface TrackingEcommercePayload {
  currency?: string;
  value?: number;
  transaction_id?: string;
  cart_id?: string | null;
  items: TrackingItem[];
}

/**
 * Vérifie si l'utilisateur a donné son consentement analytics via Tarte au Citron.
 * En cas d'absence de tarteaucitron sur la page, on considère que le consentement
 * n'est pas géré et on laisse passer (le site hôte est responsable).
 */
function hasAnalyticsConsent(): boolean {
  const tac = (window as any).tarteaucitron;
  if (!tac || !tac.state) {
    // Tarte au Citron non présent : le site hôte gère le consentement lui-même
    return true;
  }
  // Vérifier le consentement pour Google Tag Manager ou Google Analytics
  return (
    tac.state.googletagmanager === true ||
    tac.state.gtag === true ||
    tac.state.ganalytics === true
  );
}

/**
 * Envoie un événement de tracking GA4 Ecommerce via le dataLayer GTM.
 *
 * - Respecte le consentement utilisateur (Tarte au Citron)
 * - Pousse les événements dans window.dataLayer (standard GTM)
 * - Noms d'événements alignés sur le widget Regiondo
 */
export function sendTrackingEvent(
  event: TrackingEventName,
  checkpoint: TrackingCheckpoint | null = null,
  payload: TrackingEcommercePayload,
) {
  try {
    if (!hasAnalyticsConsent()) {
      if (import.meta.env.DEV) {
        console.log(
          "[Bonjour Tracking] Événement bloqué (pas de consentement):",
          event,
          checkpoint,
        );
      }
      return;
    }

    // Push dans le dataLayer GTM (standard GA4)
    const dataLayer = window.dataLayer || [];
    // Reset de l'objet ecommerce avant chaque push (recommandation Google)
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event,
      checkpoint,
      ecommerce: payload,
    });
    window.dataLayer = dataLayer;

    if (import.meta.env.DEV) {
      console.log("[Bonjour Tracking]", event, checkpoint, payload);
    }
  } catch (error) {
    console.warn("[Bonjour Tracking] Échec envoi événement:", error);
  }
}

// Fonction helper pour obtenir le prix minimum depuis le produit
// Cette donnée est utilisé pour le tracking view_item
// Utilise n'importe quel type pour éviter d'importer toute la structure Product
export function getMinPrice(product: any): number {
  let min = Infinity;
  if (product.options && Array.isArray(product.options)) {
    product.options.forEach((opt: any) => {
      if (opt.units && Array.isArray(opt.units)) {
        opt.units.forEach((unit: any) => {
          let p: number = 0;
          if (unit.promotionPrice && (unit.promotionPrice !== 0 && unit.price !== 0)) {
            p = unit.promotionPrice;
          } else {
            p = unit.price;
          }
          if (p < min) min = p;
        });
      }
    });
  }
  return min === Infinity ? 0 : min;
};